/**
 * Screenshots and voice notes for "Log a query". The person picks images or
 * audio from their device (a phone offers its camera roll) or records a voice
 * note here, up to two minutes, and can play it back or remove it before
 * sending. The engine checks every file again; these limits only save a wasted
 * upload.
 */

import { useEffect, useRef, useState } from 'react';

export const MAX_ATTACHMENTS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const MAX_RECORD_SECONDS = 120;

interface Attachment {
  file: File;
  url: string;
}

/** The first recording format this browser supports: WebM in Chrome and Android, MP4 in Safari. */
function recordingFormat(): { mimeType: string; ext: string } | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const [mimeType, ext] of [['audio/webm;codecs=opus', 'webm'], ['audio/webm', 'webm'], ['audio/mp4', 'm4a']] as const) {
    if (MediaRecorder.isTypeSupported(mimeType)) return { mimeType, ext };
  }
  return null;
}

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];
const AUDIO_EXTS = ['webm', 'm4a', 'mp3', 'mpeg', 'ogg', 'mp4'];

/** Image or audio, from the file's type, or from its name when a phone sends no type (some HEIC and M4A files). */
function kindOf(file: File): 'image' | 'audio' | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type) return null;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (IMAGE_EXTS.includes(ext)) return 'image';
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  return null;
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export default function AskAttachments({ onChange, disabled }: { onChange: (files: File[]) => void; disabled: boolean }) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const itemsRef = useRef<Attachment[]>([]);
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  const format = recordingFormat();
  const recording = seconds !== null;
  const full = items.length >= MAX_ATTACHMENTS;

  const update = (next: Attachment[]) => {
    itemsRef.current = next;
    setItems(next);
    onChange(next.map((a) => a.file));
  };

  // Release previews and the microphone when the form closes. A microphone granted
  // after this has run is released by startRecording, which checks mountedRef.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const a of itemsRef.current) URL.revokeObjectURL(a.url);
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      recorder?.stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // The recording clock; it stops the recording at the limit.
  useEffect(() => {
    if (seconds === null) return;
    if (seconds >= MAX_RECORD_SECONDS) {
      recorderRef.current?.stop();
      return;
    }
    const timer = window.setTimeout(() => setSeconds((s) => (s === null ? null : s + 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds]);

  const add = (files: File[]) => {
    setError(null);
    const next = [...itemsRef.current];
    for (const file of files) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(`Up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      if (!kindOf(file)) {
        setError(`${file.name} is not a screenshot, photo or voice note.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is over 10 MB.`);
        continue;
      }
      if (next.reduce((n, a) => n + a.file.size, 0) + file.size > MAX_TOTAL_BYTES) {
        setError('The files are too large together. Keep them under 25 MB in total.');
        continue;
      }
      next.push({ file, url: URL.createObjectURL(file) });
    }
    update(next);
  };

  const remove = (index: number) => {
    const gone = itemsRef.current[index];
    if (gone) URL.revokeObjectURL(gone.url);
    update(itemsRef.current.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    // One recording at a time: a second tap while the browser asks for the microphone
    // would open a second stream nobody could stop.
    if (!format || startingRef.current || recorderRef.current) return;
    setError(null);
    startingRef.current = true;
    setStarting(true);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (mountedRef.current) {
        setError('The microphone is blocked. Allow it for this site in your browser settings, then try again.');
      }
      return;
    } finally {
      startingRef.current = false;
      if (mountedRef.current) setStarting(false);
    }
    // The form closed while the browser was asking: release the microphone at once.
    if (!mountedRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    const recorder = new MediaRecorder(stream, { mimeType: format.mimeType });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      recorderRef.current = null;
      setSeconds(null);
      const type = format.mimeType.split(';')[0];
      const blob = new Blob(chunks, { type });
      if (blob.size === 0) return;
      const count = itemsRef.current.filter((a) => kindOf(a.file) === 'audio').length + 1;
      add([new File([blob], `Voice note ${count}.${format.ext}`, { type })]);
    };
    recorderRef.current = recorder;
    recorder.start();
    setSeconds(0);
  };

  return (
    <div className="form-group">
      <span className="form-label">Screenshots or voice notes <span className="text-muted fw-400">(optional)</span></span>
      <div className="btn-grid">
        <button type="button" className="btn btn-outline" onClick={() => fileRef.current?.click()} disabled={disabled || full || recording}>
          Add screenshot or file
        </button>
        {format && (
          recording ? (
            <button type="button" className="btn btn-primary" onClick={() => recorderRef.current?.stop()}>
              Stop recording ({clock(seconds ?? 0)})
            </button>
          ) : (
            <button type="button" className="btn btn-outline" onClick={() => void startRecording()} disabled={disabled || full || starting}>
              {starting ? 'Starting microphone…' : 'Record voice note'}
            </button>
          )
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,audio/*"
        multiple
        hidden
        onChange={(e) => {
          add(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      {items.length > 0 && (
        <ul className="ask-attach-list">
          {items.map((a, i) => (
            <li key={a.url} className="ask-attach-item">
              {kindOf(a.file) === 'image' ? (
                <img src={a.url} alt={a.file.name} className="ask-attach-thumb" />
              ) : (
                <audio src={a.url} controls className="ask-attach-audio" />
              )}
              <span className="ask-attach-name">{a.file.name}</span>
              <button type="button" className="btn btn-sm btn-outline" onClick={() => remove(i)} disabled={disabled} aria-label={`Remove ${a.file.name}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <div className="error-message mt-xs" role="alert">{error}</div>
      ) : (
        <span className="form-help">
          Up to {MAX_ATTACHMENTS} files, 10 MB each, 25 MB in total.{format ? ` Voice notes up to ${MAX_RECORD_SECONDS / 60} minutes.` : ''}
        </span>
      )}
    </div>
  );
}
